import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import type { AchievementVideo } from "../../../types/content";

/**
 * ============================================================================
 * The broadcast video — a CLICK-TO-LOAD FACADE, not an embed (D-073).
 * ============================================================================
 *
 * NOTHING THIRD-PARTY LOADS UNTIL THE VISITOR ASKS. What ships in the
 * pre-rendered HTML is a still, a button and a caption — all local, all static.
 * The `<iframe>` is created only by a click. An always-mounted YouTube embed
 * pulls several hundred kB and a tracking hop on every visit to a page most
 * visitors will read rather than watch, and it does it before they have
 * expressed any interest at all.
 *
 * ----------------------------------------------------------------------------
 * THE THUMBNAIL IS THE LOCAL STILL, AND THAT IS A DELIBERATE DEPARTURE
 * ----------------------------------------------------------------------------
 * The obvious facade uses `i.ytimg.com/vi/<id>/maxresdefault.jpg`. That would
 * reintroduce, on page load, exactly the third-party request the facade exists
 * to defer — and it can 404 (maxres does not exist for every upload), which
 * would collapse the box and produce the layout shift this component is
 * measured against. `dd-2024-1` is a frame of the same segment, already
 * content-hashed through Vite, already described by alt text the content layer
 * owns. Same picture, no request, no failure mode.
 *
 * ----------------------------------------------------------------------------
 * WHY THIS CANNOT SHIFT THE PAGE
 * ----------------------------------------------------------------------------
 * Both states are absolutely positioned inside ONE wrapper that fixes its own
 * 16:9 box. The poster does not size the container and neither does the frame,
 * so swapping them changes nothing any following element can observe. The
 * stills are 1600x900 and 1920x1080 — both 16:9 — so the picture fills the box
 * it is given.
 *
 * ----------------------------------------------------------------------------
 * WHAT A SCREEN READER GETS, AND WHY IT IS TWO THINGS
 * ----------------------------------------------------------------------------
 * The `<img>` is NOT inside the button. That is the whole trick: it keeps its
 * own descriptive `alt` and is announced as the picture it is, while the
 * transparent button over it is announced as a control named for what it DOES.
 * Nest the image in the button and the button's name swallows the description
 * or the description swallows the button's purpose; separated, both survive.
 *
 * `title` on the `<iframe>` is not optional either — an untitled frame is
 * announced as "frame" and nothing more.
 *
 * ----------------------------------------------------------------------------
 * AUTOPLAY, AND WHY prefers-reduced-motion NO LONGER GATES IT
 * ----------------------------------------------------------------------------
 * The previous version of this component autoplayed on SCROLL INTO VIEW and
 * suppressed that under `prefers-reduced-motion`, because WCAG 2.2.2 governs
 * content that starts moving without being asked. **The trigger has changed and
 * the reason has gone with it.** A visitor who activates a button named "Play…"
 * has asked, explicitly, once. Suppressing playback then would not be caution,
 * it would be ignoring them — and it would cost a second click inside a
 * third-party player whose controls we do not style. So `autoplay=1` is sent
 * unconditionally, and it is user-initiated in the sense 2.2.2 means.
 *
 * ----------------------------------------------------------------------------
 * D-057 APPLIED BEFORE THE FACT
 * ----------------------------------------------------------------------------
 * No text is laid over the picture. A contrast ratio is computed against ONE
 * ground colour and a photograph is not one colour — the mobile menu sat on a
 * hero image for months while every sweep reported the site clean. The caption
 * sits below the frame on a known token ground. The play control is not text
 * either: it is an OPAQUE disc of `--accent-interactive` carrying a glyph in
 * `--accent-interactive-contrast`, the pair the token file documents for this
 * exact purpose, so the ratio a sweep measures is disc-to-glyph and never
 * glyph-to-photograph. The ring and the shadow are what keep the disc itself
 * identifiable (WCAG 1.4.11) whatever frame it happens to sit on.
 */

interface BroadcastVideoProps {
	/** The footage, or null while an achievement has none. */
	video: AchievementVideo | null;
	/** Poster frame — a still from the segment, not a generic placeholder. */
	poster: string;
	/** Describes the poster. Required: it is a picture, in both states. */
	posterAlt: string;
	/** Rendered below the frame, never over it. */
	caption: string;
	/**
	 * The hash that means "the reader came here to watch this" (D-100). When
	 * `location.hash` matches, the recording starts without a second click.
	 * Omit it and the component is the pure click-to-load facade it has always
	 * been.
	 */
	autoPlayHash?: string;
}

/**
 * `youtube-nocookie.com`, and every parameter decided here rather than in the
 * content layer, so no entry can ship a half-configured player.
 *
 *   rel=0            keep end-cards inside this channel
 *   modestbranding=1 no YouTube wordmark over the frame
 *   playsinline=1    iOS plays in place instead of seizing the screen
 *   autoplay=1       the click WAS the request to play (see the note above)
 */
const embedSrc = (videoId: string): string =>
	`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}` +
	"?autoplay=1&rel=0&modestbranding=1&playsinline=1";

const IFRAME_ALLOW =
	"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";

export default function BroadcastVideo({
	video,
	poster,
	posterAlt,
	caption,
	autoPlayHash
}: BroadcastVideoProps): JSX.Element {
	/* Starts false on the server AND on the first client render, so the markup
	   React hydrates is the markup the pre-render wrote. A state that differed
	   between the two would cost the whole page a client re-render (D-042). */
	const [playing, setPlaying] = useState(false);
	const { hash } = useLocation();

	/* ------------------------------------------------------------------------
	   ARRIVING AT THE ANCHOR IS ALSO A REQUEST TO PLAY (D-100, owner
	   instruction: the recording should start when a reader lands here from the
	   Recognition card).

	   THIS IS AN EFFECT, NOT A RENDER-TIME READ, AND THAT IS THE WHOLE CARE IN
	   IT. Reading the hash during the first render would make the client's
	   markup differ from the pre-rendered markup on exactly the URLs this
	   feature exists for — an `<iframe>` where the build wrote a poster — and
	   React cannot adopt markup it did not produce (D-042). Starting false and
	   flipping after mount keeps hydration byte-identical and costs one frame.

	   IT DOES NOT WEAKEN THE FACADE. `/achievements` with no hash still ships
	   and stays a still, a button and a caption, with no third-party request
	   until someone asks — which is the whole of D-073's ruling. The request
	   happens only where a reader has already expressed the interest, by
	   activating a link that names the recording.

	   WHAT THE BROWSER MAY STILL DO. Autoplay policy is the user agent's, not
	   ours: an unmuted embed reached through a client-side navigation can be
	   held at a play button, and the honest failure is exactly that — the
	   player mounted, one click from playing, rather than anything broken. It
	   is deliberately not muted to force the issue: a muted news feature is
	   worse than one that needs a click. */
	useEffect(() => {
		if (!video || !autoPlayHash) return;
		if (hash.slice(1) !== autoPlayHash) return;
		setPlaying(true);
	}, [hash, autoPlayHash, video]);

	return (
		<figure className="mt-10 mb-0">
			<div className="card-shell">
				<div className="card-face overflow-hidden p-2 sm:p-3">
					{/* The box. Fixed ratio, so neither child decides the height. */}
					<div className="relative w-full overflow-hidden rounded-[10px] aspect-[16/9]">
						{playing && video ? (
							<iframe
								className="absolute inset-0 h-full w-full border-0"
								src={embedSrc(video.videoId)}
								title={video.title}
								allow={IFRAME_ALLOW}
								referrerPolicy="strict-origin-when-cross-origin"
								allowFullScreen
							/>
						) : (
							<>
								<img
									src={poster}
									alt={posterAlt}
									width={1600}
									height={900}
									loading="lazy"
									decoding="async"
									className="absolute inset-0 h-full w-full object-cover"
								/>
								{video && (
									<button
										type="button"
										onClick={() => setPlaying(true)}
										aria-label={`Play the recording: ${video.title}`}
										className="group absolute inset-0 grid w-full place-items-center rounded-[10px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)] focus-visible:ring-offset-0"
									>
										<span className="grid h-16 w-16 place-items-center rounded-full bg-[var(--accent-interactive)] ring-2 ring-[var(--accent-interactive-contrast)] shadow-[0_6px_24px_rgba(0,0,0,0.45)] transition-transform duration-200 group-hover:scale-110 sm:h-20 sm:w-20">
											{/* A triangle, not a glyph — no font, no text node,
											    nothing for a contrast sweep to score against
											    the photograph behind the disc. */}
											<svg
												viewBox="0 0 24 24"
												aria-hidden="true"
												focusable="false"
												className="ml-1 h-7 w-7 sm:h-9 sm:w-9"
												fill="var(--accent-interactive-contrast)"
											>
												<path d="M8 5v14l11-7z" />
											</svg>
										</span>
									</button>
								)}
							</>
						)}
					</div>
				</div>
			</div>

			<figcaption className="mt-3 text-center text-sm text-[var(--text-muted)]">
				{caption}
				{video && (
					<>
						{" "}
						{/* A REAL LINK, OUTSIDE THE BUTTON. It is the whole no-JS path,
						    and nesting it in the button would be invalid HTML that a
						    parser rewrites — which disables hydration for the page
						    (D-042 found exactly one such element and it cost the site
						    every route's markup). */}
						<a
							href={video.watchUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="text-[var(--accent-link)] underline underline-offset-2 transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
						>
							Watch on YouTube
						</a>
						.
					</>
				)}
			</figcaption>
		</figure>
	);
}
